import React from 'react';
import { Marker } from 'react-simple-maps';
import {
  FOG_800M_COLOR
} from '../../theme/colors';
import {
  AIRPORT_DOT_OFFSETS,
  AIRPORT_LABEL_POSITIONS,
  PULSE_ANIMATION,
  MARKER_SIZES,
  AIRPORT_LABEL_STYLE,
  MAP_COMPONENT_STYLES
} from '../../constants/mapConstants';

interface Airport {
  id: number;
  code: string;
  name: string;
  coordinates: [number, number];
}

interface AirportStatus {
  actualColor: string;
  forecastColor: string;
}

interface AirportMarkerProps {
  airport: Airport;
  status: AirportStatus;
  isSelected: boolean;
  onMouseEnter: (e: React.MouseEvent) => void;
  onMouseLeave: () => void;
  onClick: () => void;
}

// Helper to get airport dot offset
function getAirportDotOffset(airportCode: string): number {
  if (airportCode === 'NZOH') return AIRPORT_DOT_OFFSETS.NZOH;
  if (airportCode === 'NZPM') return AIRPORT_DOT_OFFSETS.NZPM;
  return AIRPORT_DOT_OFFSETS.DEFAULT;
}

const AirportMarker: React.FC<AirportMarkerProps> = ({
  airport,
  status,
  isSelected,
  onMouseEnter,
  onMouseLeave,
  onClick
}) => {
  // Calculate label position once
  const labelPosition = AIRPORT_LABEL_POSITIONS[airport.code] || AIRPORT_LABEL_POSITIONS.DEFAULT;

  return (
    <Marker
      coordinates={airport.coordinates}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      <g style={MAP_COMPONENT_STYLES.markerGroup}>
        {/* Wrap circles in a transform group to move dots independently for NZOH (left) and NZPM (right) */}
        <g transform={`translate(${getAirportDotOffset(airport.code)}, 0)`}>
          {/* Pulsing animation for severe fog in either actual or forecast */}
          {(status.actualColor === FOG_800M_COLOR || status.forecastColor === FOG_800M_COLOR) && (
            <circle
              cx={0}
              cy={0}
              r={MARKER_SIZES.radius}
              fill={FOG_800M_COLOR}
              opacity={PULSE_ANIMATION.initialOpacity}
              stroke="none"
            >
              <animate
                attributeName="r"
                from={PULSE_ANIMATION.radiusFrom}
                to={PULSE_ANIMATION.radiusTo}
                dur={PULSE_ANIMATION.duration}
                repeatCount="indefinite"
              />
              <animate
                attributeName="opacity"
                from={PULSE_ANIMATION.opacityFrom}
                to={PULSE_ANIMATION.opacityTo}
                dur={PULSE_ANIMATION.duration}
                repeatCount="indefinite"
              />
            </circle>
          )}
          {/* Show full circle if colors match, otherwise show two half circles */}
          <g>
            {status.actualColor === status.forecastColor ? (
              /* Full circle when current and forecast match */
              <circle
                cx={0}
                cy={0}
                r={MARKER_SIZES.radius}
                fill={status.actualColor}
                stroke="#FFF"
                strokeWidth={isSelected ? MARKER_SIZES.strokeWidthSelected : MARKER_SIZES.strokeWidthNormal}
              />
            ) : (
              <>
                {/* Two half circles when current and forecast differ */}
                <defs>
                  <clipPath id={`leftHalf-${airport.code}`}>
                    <rect x="-10" y="-10" width="10" height="20" />
                  </clipPath>
                  <clipPath id={`rightHalf-${airport.code}`}>
                    <rect x="0" y="-10" width="10" height="20" />
                  </clipPath>
                </defs>

                {/* Left half circle - Current conditions */}
                <circle
                  cx={0}
                  cy={0}
                  r={MARKER_SIZES.radius}
                  fill={status.actualColor}
                  clipPath={`url(#leftHalf-${airport.code})`}
                />

                {/* Right half circle - Forecast */}
                <circle
                  cx={0}
                  cy={0}
                  r={MARKER_SIZES.radius}
                  fill={status.forecastColor}
                  clipPath={`url(#rightHalf-${airport.code})`}
                />

                {/* White outline and divider */}
                <circle
                  cx={0}
                  cy={0}
                  r={MARKER_SIZES.radius}
                  fill="none"
                  stroke="#FFF"
                  strokeWidth={isSelected ? MARKER_SIZES.strokeWidthSelected : MARKER_SIZES.strokeWidthNormal}
                />
                <line
                  x1={0}
                  y1={-MARKER_SIZES.radius}
                  x2={0}
                  y2={MARKER_SIZES.radius}
                  stroke="#FFF"
                  strokeWidth={MARKER_SIZES.dividerStrokeWidth}
                  opacity={MARKER_SIZES.dividerOpacity}
                />
              </>
            )}
          </g>
        </g>{/* Close the transform group for dot positioning */}
        <text
          textAnchor={labelPosition.textAnchor}
          x={labelPosition.x}
          y={labelPosition.y}
          style={AIRPORT_LABEL_STYLE}
        >
          {airport.code}
        </text>
      </g>
    </Marker>
  );
};

export default AirportMarker;